import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InputLabel, Select, MenuItem, FormControl, FormHelperText, ListSubheader } from '@mui/material';
import { makeStyles } from '@mui/styles';
import useAppLanguage from '@/hooks/useAppLanguage';
import HighlightText from '@/components/HighlightText/HighlightText';
import MandatoryLabel from '@/components/UI//MandatoryLabel/MandatoryLabel';
import { fieldTypes } from '@/helpers/constants';
import { getFieldCursorType } from '@/helpers/utils';
import { useFieldSelection } from '@/hooks/useFieldSelection';
import useWarningField from '@/hooks/useWarningField';
import { InputVariants } from '@/types';
import dropdownStyles from './Dropdown.module.scss';

const useStyles = makeStyles(() => ({
  dropdownStyle: {
    '& li': {
      height: 40,
    },
  },
  menuCategory: {
    color: 'black',
    textAlign: 'center',
    fontWeight: 'bold',
    position: 'relative',
    lineHeight: '2.5rem',
    backgroundColor: 'gainsboro',
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '20%',
      borderBottom: '1px solid #000',
      margin: '0 10px 3px 0px',
    },
    '&::after': {
      content: '""',
      display: 'inline-block',
      width: '20%',
      borderBottom: '1px solid #000',
      margin: '0 0px 3px 10px',
    },
  },
  menuCategorySubCR: {
    '&::before': {
      width: '15%',
    },
    '&::after': {
      width: '15%',
    },
  },
}));

interface DropdownProps {
  id: string;
  label?: string | ReactNode;
  options: string[] | { [key: string]: { [key: string]: string } };
  // best form, unlimited properties
  value: string;
  handleChange?: (event: React.ChangeEvent<HTMLInputElement>, child?: React.ReactNode) => void;
  helperText?: string;
  variant?: InputVariants;
  formControlClass?: string;
  isMandatory?: boolean;
  disabled?: boolean;
  invalid?: boolean;
  onBlur?: () => void;
  withoutTranslations?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  language?: string;
  placeholder?: string;
  optionPlaceholderActive?: boolean;
  fieldPath?: string[];
  categoriesConfig?: { beforeKey: string; translationKey: string }[];
  size?: 'small' | 'medium';
}

const Dropdown: React.FC<DropdownProps> = (props) => {
  const { id, label = '', options = [], value, handleChange = () => { }, helperText, variant = 'outlined',
    formControlClass, isMandatory = false, disabled = false, invalid = false, onBlur = () => { }, withoutTranslations = false,
    onOpen = () => null, onClose = () => null, language, placeholder, optionPlaceholderActive, fieldPath = [],
    categoriesConfig, size } = props;

  const inputLabel = isMandatory ? (<>{label}<MandatoryLabel /></>) : label;
  const lang = useAppLanguage();
  const classes = useStyles();
  const { t } = useTranslation();
  const [tab, fieldName, fieldSecondaryName = ''] = fieldPath;
  const { handleFieldSelection, isFieldSelected, isSelectionModeOn } = useFieldSelection({ tab, fieldName, fieldSecondaryName });
  const warning = useWarningField(fieldPath, fieldTypes.DROPDOWN) || invalid || isFieldSelected;

  const getRenderValue = (selected: string) => {
    if ((selected && selected !== 'placeholder')) {
      return (
        <HighlightText
          fieldId={id}
          fieldPath={fieldPath}
          fieldType={fieldTypes.DROPDOWN}
        >
          {withoutTranslations ? selected : (options?.[selected]?.[lang] ?? selected)}
        </HighlightText>
      );
    }

    if (selected === 'placeholder' && (!label && placeholder)) {
      return placeholder;
    }

    return null;
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isFieldSelected) {
      handleFieldSelection(e);
    }

    handleChange(e);
  };

  return (
    <FormControl
      error={warning}
      variant={variant}
      className={`${formControlClass} ${warning ? 'ValidationIndicatorField' : ''}`}
      onClick={isSelectionModeOn ? handleFieldSelection : undefined}
      sx={{ '& *': getFieldCursorType(isSelectionModeOn) }}
    >
      {!!label && (
        <InputLabel id={`${id}-label`}>
          {inputLabel}
        </InputLabel>
      )}
      <Select
        className={`${warning ? 'validation-warning' : ''} ${(value === 'placeholder' ? dropdownStyles.Placeholder : '')}`}
        disabled={disabled}
        labelId={`${id}-label`}
        label={inputLabel}
        id={`${id}-select`}
        data-testid={`${id}-select`}
        inputProps={{
          id: `${id}-select-input`,
          'data-testid': `${id}-select-input`,
        }}
        size={size}
        value={value}
        renderValue={(selected) => getRenderValue(selected)}
        onOpen={onOpen}
        onClose={onClose}
        onChange={handleOnChange}
        onBlur={onBlur}
        MenuProps={{
          classes: {
            paper: classes.dropdownStyle,
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
        }}
      >
        {(!label && placeholder) && (
          <MenuItem value="placeholder" disabled={!optionPlaceholderActive}>
            {placeholder}
          </MenuItem>
        )}
        {(withoutTranslations ? options : Object.keys(options)).map((option, index) => (
          [
            categoriesConfig && categoriesConfig.map((category, index2) => {
              if (category.beforeKey === (withoutTranslations ? option : Object.keys(options)[index])) {
                return (
                  <ListSubheader key={category.beforeKey} classes={{ root: `${classes.menuCategory} ${index2 === 0 ? '' : classes.menuCategorySubCR}` }}>
                    {t(category.translationKey)}
                  </ListSubheader>
                );
              }

              return null;
            }),
            <MenuItem
              key={option}
              value={option}
            >
              {withoutTranslations ? option : options?.[option]?.[lang]}
            </MenuItem>,
          ]
        ))}
      </Select>
      {warning && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default Dropdown;
